/**
 * Confetti (F-203 / US-173 / T-173-001) — dependency-free celebration burst.
 *
 * Pure CSS/DOM: renders a fixed full-viewport overlay of ~120 paper pieces
 * that fall with randomized color/rotation/drift, then detaches itself once
 * the animation window closes. No canvas, no external package — keeps the
 * bundle lean and the build sd-buildable anywhere.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

export const CONFETTI_PIECE_COUNT = 120;
export const CONFETTI_LIFETIME_MS = 2600;

const COLORS = [
  '#f59e0b',
  '#10b981',
  '#6366f1',
  '#ef4444',
  '#ec4899',
  '#06b6d4',
  '#8b5cf6',
];

export interface ConfettiPiece {
  id: number;
  left: number; // vw %
  delay: number; // ms before falling starts
  duration: number; // ms
  size: number; // px
  color: string;
  rotate: number; // deg, starting rotation
  spin: number; // deg, rotation difference across the fall
}

export function makeConfettiPieces(count: number = CONFETTI_PIECE_COUNT): ConfettiPiece[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 300,
    duration: 900 + Math.random() * 1200,
    size: 7 + Math.random() * 7,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    rotate: Math.random() * 360,
    spin: 240 + Math.random() * 480,
  }));
}

interface ConfettiProps {
  count?: number;
  lifetimeMs?: number;
}

const Confetti: React.FC<ConfettiProps> = ({ count = CONFETTI_PIECE_COUNT, lifetimeMs = CONFETTI_LIFETIME_MS }) => {
  const pieces = useMemo(() => makeConfettiPieces(count), [count]);
  const [gone, setGone] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    timer.current = window.setTimeout(() => setGone(true), lifetimeMs);
    return () => window.clearTimeout(timer.current);
  }, [lifetimeMs]);

  if (gone) return null;

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50" data-testid="confetti" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          data-testid="confetti-piece"
          style={{
            position: 'absolute',
            top: '-20px',
            left: `${p.left}vw`,
            width: `${p.size}px`,
            height: `${p.size * 0.4}px`,
            background: p.color,
            borderRadius: '1px',
            transform: `rotate(${p.rotate}deg)`,
            animation: `confetti-fall ${p.duration}ms ${p.delay}ms cubic-bezier(0.3, 0.2, 0.6, 1) forwards`,
            ['--confetti-spin' as string]: `${p.spin}deg`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) translateX(0) rotate(var(--init, 0deg));
            opacity: 1;
          }
          100% {
            transform: translateY(105vh) translateX(8vw) rotate(var(--confetti-spin, 360deg));
            opacity: 0.85;
          }
        }
      `}</style>
    </div>
  );
};

export default Confetti;